import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { format } from 'date-fns';
import { aiMonitoringAPI } from '../../services';
import PaginationComponent from '../../components/PaginationComponent';

/**
 * #181800 (lệnh sếp 18/10/2026) — LỊCH SỬ NHẬN DIỆN: mỗi lượt quét × vùng sếp ĐÃ KHOANH có khách (ảnh +
 * khung vùng, bàn, số người, % AI tin có khách, đã gửi tin chưa). Lọc theo bàn, vùng khoanh (camera + tên
 * khung) và ngày. Bố cục: dải số liệu → bộ lọc có nhãn → lưới thẻ cùng chiều cao → phân trang.
 */
const PAGE_SIZE = 12;
const EMPTY = { table: '', region: '', from: '', to: '' };

const fmt = (v, p = 'HH:mm dd/MM/yyyy') => {
  if (!v) return '—';
  try {
    return format(new Date(v), p);
  } catch {
    return '—';
  }
};

// Ảnh cần JWT nên tải qua apiClient (blob) rồi dựng URL tạm; giải phóng khi thẻ biến mất.
const DetectionImage = ({ id, branchId, box }) => {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url = null;
    let alive = true;
    setSrc(null);
    setFailed(false);
    aiMonitoringAPI
      .getDetectionImage(id, branchId)
      .then((blob) => {
        if (!alive) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [id, branchId]);

  if (failed) return <span className="cambill-img-empty">Ảnh đã quá hạn lưu trên máy camera</span>;
  if (!src) return <Spinner size="sm" animation="border" variant="light" />;
  const b = box && ['x', 'y', 'w', 'h'].every((k) => Number.isFinite(Number(box[k]))) ? box : null;
  return (
    <a href={src} target="_blank" rel="noreferrer" className="cambill-img-wrap" title="Mở ảnh lớn">
      <img src={src} alt="Ảnh khu vực nhận diện" loading="lazy" />
      {b && (
        <span
          className="cambill-box"
          data-testid="khung-vung"
          style={{ left: `${b.x * 100}%`, top: `${b.y * 100}%`, width: `${b.w * 100}%`, height: `${b.h * 100}%` }}
        />
      )}
    </a>
  );
};

const Stat = ({ value, label }) => (
  <div className="cambill-stat">
    <div className="cambill-stat-value">{value}</div>
    <div className="cambill-stat-label">{label}</div>
  </div>
);

const AiDetectionHistory = ({ branchId }) => {
  const [filters, setFilters] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!branchId) return;
    try {
      setLoading(true);
      setError(null);
      setData(
        await aiMonitoringAPI.getDetections({
          branch_id: branchId,
          table_number: filters.table || undefined,
          region: filters.region || undefined,
          from: filters.from ? new Date(`${filters.from}T00:00:00`).toISOString() : undefined,
          to: filters.to ? new Date(`${filters.to}T23:59:59`).toISOString() : undefined,
          page,
          limit: PAGE_SIZE,
        }),
      );
    } catch (err) {
      setError(err?.message || 'Không tải được lịch sử nhận diện');
    } finally {
      setLoading(false);
    }
  }, [branchId, filters, page]);

  useEffect(() => {
    load();
  }, [load]);

  const change = (patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const filtering = Object.values(filters).some(Boolean);
  const items = data?.items ?? [];
  const tables = data?.filters?.tables ?? [];
  const regions = data?.filters?.regions ?? [];

  if (!branchId) return <p className="text-muted">Chọn chi nhánh để xem lịch sử nhận diện.</p>;

  return (
    <div data-testid="lich-su-nhan-dien">
      <div className="cambill-toolbar">
        <p className="cambill-hint mb-0">
          Mỗi lượt camera quét thấy khách trong vùng đã khoanh (khung đỏ). Vùng chưa khoanh không được ghi nhận.
          Ảnh lấy lại từ máy camera, giữ khoảng 8 ngày.
        </p>
        <Button size="sm" variant="outline-primary" onClick={load} disabled={loading}>
          {loading ? <Spinner size="sm" animation="border" /> : 'Làm mới'}
        </Button>
      </div>

      {data && (
        <div className="cambill-stats" data-testid="dai-so-lieu">
          <Stat value={data.total} label={filtering ? 'Lượt khớp bộ lọc' : 'Tổng lượt nhận diện'} />
          <Stat value={data.summary?.notified ?? 0} label="Lượt đã gửi tin" />
          <Stat value={regions.length} label="Vùng khoanh có khách" />
          <Stat value={fmt(data.summary?.last_detected_at, 'HH:mm dd/MM')} label="Lượt gần nhất" />
        </div>
      )}

      <Card className="mb-3">
        <Card.Body className="py-3">
          <Row className="g-3 align-items-end">
            <Col xs={12} sm={6} lg={3}>
              <Form.Label htmlFor="cambill-loc-vung">Vùng khoanh</Form.Label>
              <Form.Select
                id="cambill-loc-vung"
                aria-label="Lọc theo vùng khoanh"
                value={filters.region}
                onChange={(e) => change({ region: e.target.value })}
              >
                <option value="">Tất cả vùng</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>{r.label} · cam {r.camera_serial}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} sm={6} lg={2}>
              <Form.Label htmlFor="cambill-loc-ban">Bàn</Form.Label>
              <Form.Select
                id="cambill-loc-ban"
                aria-label="Lọc theo bàn"
                value={filters.table}
                onChange={(e) => change({ table: e.target.value })}
                disabled={!tables.length && !filters.table}
                title={tables.length ? undefined : 'Chưa vùng nào được gắn số bàn ở màn Khoanh vùng camera'}
              >
                <option value="">{tables.length ? 'Tất cả bàn' : 'Chưa gắn số bàn'}</option>
                {tables.map((n) => (
                  <option key={n} value={n}>Bàn {n}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} lg={2}>
              <Form.Label htmlFor="cambill-tu">Từ ngày</Form.Label>
              <Form.Control id="cambill-tu" type="date" aria-label="Từ ngày" value={filters.from}
                onChange={(e) => change({ from: e.target.value })} />
            </Col>
            <Col xs={6} lg={2}>
              <Form.Label htmlFor="cambill-den">Đến ngày</Form.Label>
              <Form.Control id="cambill-den" type="date" aria-label="Đến ngày" value={filters.to}
                onChange={(e) => change({ to: e.target.value })} />
            </Col>
            <Col xs={12} lg={3} className="d-flex justify-content-lg-end">
              <Button variant="link" className="px-0" onClick={() => change(EMPTY)} disabled={!filtering}>
                Xoá bộ lọc
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <p className="text-danger">{error}</p>}
      {loading && !data && (
        <div className="text-center py-5"><Spinner size="sm" animation="border" /> Đang tải...</div>
      )}
      {data && !items.length && !loading && (
        <Card className="text-center text-muted py-5" data-testid="lich-su-trong">
          Chưa có lượt nhận diện nào{filtering ? ' khớp bộ lọc' : ''}.
        </Card>
      )}

      <Row xs={1} sm={2} xl={3} className="g-3">
        {items.map((it) => (
          <Col key={it.id}>
            <Card className="h-100 cambill-card" data-testid="the-nhan-dien">
              <Card.Body>
                <div className="cambill-card-head">
                  <span className="cambill-card-title" title={it.region_label || ''}>
                    {it.region_label || 'Khu vực'}
                    {it.table_number != null && <span className="text-muted fw-normal"> · Bàn {it.table_number}</span>}
                  </span>
                  <span className="cambill-card-time">{fmt(it.detected_at)}</span>
                </div>
                <div className="cambill-img-frame">
                  {it.has_image ? (
                    <DetectionImage id={it.id} branchId={branchId} box={it.box} />
                  ) : (
                    <span className="cambill-img-empty">Lượt này không có ảnh</span>
                  )}
                </div>
                <div className="cambill-meta">
                  <Badge bg="secondary">{it.people_count} người</Badge>
                  {it.confidence != null && (
                    <Badge bg="info" data-testid="do-tin-cay-lich-su">
                      Có khách {Math.round(it.confidence * 100)}%
                    </Badge>
                  )}
                  {it.notified && <Badge bg="success">Đã gửi tin</Badge>}
                  {it.lan_nhan_dang > 0 && (
                    <Badge bg="warning" text="dark">Nhận dạng lại lần {it.lan_nhan_dang}</Badge>
                  )}
                </div>
                {it.camera_serial && <div className="cambill-cam">Camera {it.camera_serial}</div>}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {data && data.total > PAGE_SIZE && (
        <div className="mt-3">
          <PaginationComponent
            totalItems={data.total}
            itemsPerPage={PAGE_SIZE}
            currentPage={page}
            onPageChange={setPage}
            loading={loading}
          />
        </div>
      )}
    </div>
  );
};

export default AiDetectionHistory;
